import React, { useState } from 'react';
import { OverUnderScoringOption } from './OverUnderScoringOption';
import type { OverUnderScoringQuestionProps } from '@/utils/scoring/adapters/overUnderScoringAdapter';
import type { MatchStatsResponse } from '@/api/feed/getFeedMatchStats';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Pencil, X } from 'lucide-react';

interface OverUnderScoringQuestionComponentProps extends OverUnderScoringQuestionProps {
  questionId?: string;
  fixtureId?: string;
  onMarkCorrect?: (optionId: string, isCorrect: boolean) => void;
  onSave?: (optionId: string) => void;
  onCancel?: (optionId: string) => void;
  language?: 'en' | 'fr';
  manualScoring?: boolean;
  matchStats?: MatchStatsResponse;
  onStatChange?: (optionId: string, statValue: number) => void;
  onOverrideSuccess?: () => void;
}

export function OverUnderScoringQuestion({
  questionId,
  fixtureId,
  questionText: _questionText,
  questionSubtext: _questionSubtext,
  questionNumber: _questionNumber,
  options,
  points: _points,
  status: _status,
  statTypeCode,
  onMarkCorrect: _onMarkCorrect,
  onSave,
  onCancel,
  language = 'en',
  manualScoring = false,
  matchStats,
  onStatChange,
  onOverrideSuccess,
}: OverUnderScoringQuestionComponentProps) {
  // Extract stat value from the first option (all options should have the same stat value)
  let statValue: number | null = null;
  if (options.length > 0 && options[0].stats) {
    const statsObj = options[0].stats;
    if (typeof statsObj === 'object' && !Array.isArray(statsObj)) {
      const values = Object.values(statsObj);
      const numericValue = values.find((v): v is number => typeof v === 'number');
      statValue = numericValue ?? null;
    }
  }

  const displayValue = statValue !== null ? statValue.toString() : '0';
  const [isEditing, setIsEditing] = useState(false);
  const [editingValue, setEditingValue] = useState(displayValue);

  // Update editing value when stat value changes externally
  React.useEffect(() => {
    setEditingValue(displayValue);
  }, [displayValue]);

  const handleEditClick = () => {
    if (manualScoring) {
      setEditingValue(displayValue);
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    const numValue = parseInt(editingValue, 10);
    if (!isNaN(numValue) && onStatChange && options.length > 0 && options[0].option_id) {
      // Update stat for all options (they all share the same stat value)
      options.forEach(option => {
        if (option.option_id) {
          onStatChange(option.option_id, numValue);
        }
      });
    }
    if (onSave && options.length > 0 && options[0].option_id) {
      onSave(options[0].option_id);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditingValue(displayValue);
    setIsEditing(false);
    if (onCancel && options.length > 0 && options[0].option_id) {
      onCancel(options[0].option_id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className="space-y-3">
      {/* Options - Side by Side Layout */}
      <div className="flex gap-4">
        {options.map((option, index) => (
          <OverUnderScoringOption
            key={option.option_id || index}
            questionId={questionId}
            fixtureId={fixtureId}
            statTypeCode={statTypeCode}
            option={option}
            language={language}
            manualScoring={manualScoring}
            matchStats={matchStats}
            onStatChange={onStatChange}
            onSave={onSave}
            onCancel={onCancel}
            onOverrideSuccess={onOverrideSuccess}
          />
        ))}
      </div>

      {/* Center-aligned stat value display */}
      <div className="flex justify-center items-center gap-2">
        {isEditing ? (
          <>
            <Input
              type="number"
              value={editingValue}
              onChange={e => setEditingValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="h-8 w-20 text-center text-2xl font-semibold"
              autoFocus
            />
            <Button
              variant="default"
              size="sm"
              onClick={handleSave}
              className="h-8 px-2 text-xs bg-blue-600 hover:bg-blue-700"
            >
              Save
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="h-8 w-8 p-0 hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </Button>
          </>
        ) : (
          <>
            <span className="text-2xl font-semibold text-gray-900">{displayValue}</span>
            {manualScoring && (
              <button
                onClick={handleEditClick}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                aria-label="Edit stat value"
              >
                <Pencil className="h-4 w-4 text-purple-600" />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
